/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */

import type { ActionSource } from './ActionSource';
import type { ArchiveReason } from './ArchiveReason';
import type { ChosenResourceData } from './ChosenResourceData';
import type { OrderStatus } from './OrderStatus';
import type { ReferralSource } from './ReferralSource';
import type { ResourceHoldData } from './ResourceHoldData';

/**
 * OrderData schema for webhooks containing only relevant fields for external integrations
 */
export type OrderWebhookData = {
  /**
   * The event that triggered this webhook, for example order.created
   */
  type: OrderWebhookData.type;
  /**
   * Tenant account identifier
   */
  account_id: string;
  /**
   * Specific experience internal id
   */
  order_page_id: string;
  /**
   * Event internal ID for event orders.None for private orders
   */
  parent_id?: string;
  /**
   * Unique internal order ID
   */
  id: string;
  /**
   * Sequential order number
   */
  order_number?: number;
  /**
   * Experience date in YYYY-MM-DD format
   */
  date?: string;
  /**
   * Experience start time in seconds from midnight
   */
  time?: number;
  /**
   * Experience duration in seconds
   */
  duration: number;
  /**
   * Customer first name
   */
  firstname: string;
  /**
   * Customer last name
   */
  lastname: string;
  /**
   * Customer email address
   */
  email: string;
  /**
   * Customer phone number
   */
  phone: string;
  /**
   * Customer booking communication language
   */
  language: string;
  /**
   * Base price. Apply discount if exists
   */
  price: number;
  /**
   * Number of participants booked
   */
  persons: number;
  /**
   * Current order status. Possible values: 'requested' (initial booking request, pending payment), 'paid' (payment completed), 'confirmed' (confirmed without a payment processed by us - manual backoffice order, free order, or API resolution), 'paid_with_voucher' (paid using voucher), 'converted_to_voucher' (order converted to voucher), 'converted_from_voucher' (order created from voucher), 'payment_link_generated' (payment link created), 'converted_from_waitlist' (moved from waiting list), 'paid_with_payment_link' (paid via payment link), 'paid_with_pass' (redeemed with a pass), 'paid_with_external_voucher' (third-party voucher accepted), 'deposit_paid' (deposit collected, balance open), 'pending_confirmation' (provisional order created via API, awaiting resolution), 'abandoned' (checkout abandoned), 'archived' (order archived)
   */
  status: OrderStatus;
  /**
   * Order creation timestamp
   */
  created_at: string;
  /**
   * Custom booking form fields
   */
  custom_details?: null;
  /**
   * Custom personal details from booking form
   */
  custom_personal_details?: null;
  /**
   * List of assigned employee IDs
   */
  employee_ids: Array<string>;
  /**
   * Applied voucher ID if any
   */
  voucher_id?: string;
  /**
   * Applied discount percentage
   */
  discount_percentage?: number;
  /**
   * Applied fixed discount amount
   */
  discount_fixed_value?: number;
  /**
   * Tip amount in smallest currency unit
   */
  tip_amount?: number;
  /**
   * How the customer found the business. Possible values: 'social_media' (Facebook, Instagram, etc.), 'google' (Google search/ads), 'newsletter' (email marketing), 'word_of_mouth' (friend recommendation), 'review_sites' (TripAdvisor, Yelp, etc.), 'returning' (previous customer), 'other' (unspecified source)
   */
  referral_source?: ReferralSource;
  /**
   * UTM tracking parameters from marketing campaigns. Contains utm_source (traffic source like 'google', 'facebook'), utm_medium (marketing medium like 'cpc', 'email'), utm_campaign (campaign name), utm_term (paid keywords), utm_content (ad content variation). Example: {'utm_source': 'google', 'utm_medium': 'cpc', 'utm_campaign': 'summer_workshops'}
   */
  utm_data: null;
  /**
   * Browser session ID for analytics
   */
  session_id?: string;
  /**
   * Indexes of allocated resources
   */
  resource_indexes?: Array<number>;
  /**
   * Customer-selected resource option restricting which of the page's resources the order may use
   */
  resource_option_id?: string;
  /**
   * Admin-chosen seating, one hold per scheduler config. Always pinned: the scheduler never moves it and never seats this order on its own while holds exist.
   */
  manual_resource_holds?: Array<ResourceHoldData>;
  /**
   * Chosen amount of resources
   */
  chosen_resources?: Array<ChosenResourceData>;
  /**
   * Why the order was archived
   */
  archive_reason?: ArchiveReason;
  /**
   * Internal management comment
   */
  comment?: string;
  /**
   * Customer consent for marketing communications
   */
  can_spam?: boolean;
  /**
   * Applied discount ID
   */
  discount_id?: string;
  /**
   * Previous order ID for returning customers
   */
  returning_customer_from_order_id?: string;
  /**
   * Deposit amount charged when order uses a deposit instead of full price
   */
  deposit_price?: number;
  /**
   * Extra items total minus everything already paid; None when the order has no extra items or the value was not computed for this endpoint, negative means credit
   */
  balance_due?: number;
  /**
   * How many times the order's open balance was settled; increases by one on each order.balance_settled event
   */
  balance_settled_count?: number;
  /**
   * UTC time the open balance was last settled
   */
  balance_settled_at?: string;
  /**
   * UTC time when a pending order (payment_link_generated / pending_confirmation) stops blocking the calendar and is auto-cancelled; None means it blocks until resolved
   */
  pending_expires_at?: string;
  /**
   * Identity behind the session or API key that resolved the pending_confirmation order; None for org-wide API keys with no attached user
   */
  resolved_by_user_id?: string;
  /**
   * Channel through which the order was created as pending or confirmed: customer (free checkout), staff (backoffice), or api (API creation or pending-order resolution). None for orders settled via payment statuses or confirmed before this field existed
   */
  source?: ActionSource;
};

export namespace OrderWebhookData {
  /**
   * The event that triggered this webhook, for example order.created
   */
  export enum type {
    ORDER_CREATED = 'order.created',
    ORDER_UPDATED = 'order.updated',
    ORDER_BALANCE_SETTLED = 'order.balance_settled',
  }
}
